import React from 'react';
import { CompactError, RobbieOverlay, Skeleton } from '../../../components/ui';
import { analysisCopy } from '../../../utils/waitCopy';

/**
 * The analysis wait on step 3: a placeholder shaped like the questions card
 * under a Robbie overlay saying what Robbie is going through and the usual
 * range. A failed run shows in the same card with one way forward — run it
 * again. Render it inside a `relative` host (the step's main column).
 */
export interface AnalysisWaitProps {
  /** The run is going (or about to). */
  running: boolean;
  failed: boolean;
  components: number;
  /** Past the usual range. */
  long?: boolean;
  onRetry?: () => void;
  /** A retry was refused or could not be sent. */
  retryError?: string | null;
}

/** Shaped like the questions card, so nothing jumps when the questions land. */
export const QuestionsSkeleton: React.FC = () => (
  <div className="flex flex-col gap-[14px] rounded-card border border-line bg-surface px-s6 py-[18px]">
    <Skeleton width="36%" height={13} />
    <Skeleton width="58%" />
    {[0, 1, 2].map((row) => (
      <div key={row} className="mt-s2 flex items-center gap-s5">
        <Skeleton shape="block" width={28} height={28} />
        <div className="flex flex-1 flex-col gap-s2">
          <Skeleton width="44%" />
          <Skeleton width="70%" />
        </div>
        <Skeleton shape="block" width={92} height={28} />
      </div>
    ))}
  </div>
);

export const AnalysisWaitOverlay: React.FC<AnalysisWaitProps> = ({
  running,
  failed,
  components,
  long = false,
  onRetry,
  retryError,
}) => (
  <RobbieOverlay
    open={running || failed}
    title={analysisCopy.title(components)}
    sub={analysisCopy.sub}
    usual={analysisCopy.usual(components)}
    long={long}
    leave
    error={
      failed ? (
        <CompactError
          title={analysisCopy.failedTitle}
          message={retryError || analysisCopy.failedMessage}
          action={onRetry ? { label: analysisCopy.retry, onClick: onRetry } : undefined}
        />
      ) : undefined
    }
  />
);

/** The whole wait as one block, for a sub-step that has nothing else to show yet. */
export const AnalysisWait: React.FC<AnalysisWaitProps> = (props) => (
  <div className="relative min-h-[420px]">
    <QuestionsSkeleton />
    <AnalysisWaitOverlay {...props} />
  </div>
);

export default AnalysisWait;
